import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { fetchArchivedSessions, formatArchiveTimestamp, type ArchivedSession } from "@/lib/sessionArchive";

interface Props {
  limit?: number;
}

const RecentSessionsModule = ({ limit = 4 }: Props) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ArchivedSession[] | null>(null);

  useEffect(() => {
    if (!user) return;
    fetchArchivedSessions(user.id, limit).then(setSessions).catch(() => setSessions([]));
  }, [user, limit]);

  if (!user) return null;

  if (sessions === null) {
    return (
      <div className="flex items-center justify-center py-6 opacity-60">
        <Loader2 className="w-3.5 h-3.5 text-cyan/60 animate-spin" />
      </div>
    );
  }

  if (sessions.length === 0) return null;

  return (
    <div>
      <div className="flex items-baseline justify-between mb-5">
        <h2 className="text-[9px] uppercase tracking-[0.42em] text-muted-foreground/40 font-mono">
          Recent Sessions
        </h2>
        <button
          onClick={() => navigate("/rehearsal/archive")}
          className="text-[9px] font-mono tracking-[0.32em] uppercase text-muted-foreground/45 hover:text-muted-foreground/80 transition-colors"
        >
          Open Archive →
        </button>
      </div>

      <div className="space-y-1.5">
        {sessions.map((s) => (
          <button
            key={s.id}
            onClick={() => navigate(`/rehearsal/debrief/${s.id}`)}
            className="w-full text-left rounded-lg border border-border/20 bg-card/15 hover:bg-card/30 hover:border-border/40 transition-all duration-500 px-4 py-3.5 group"
          >
            <div className="space-y-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-[13px] text-foreground/65 font-light truncate group-hover:text-foreground/85 transition-colors">
                  {s.scenarioLabel}
                </span>
                <span className="text-[9px] font-mono tracking-[0.28em] uppercase text-muted-foreground/30 shrink-0">
                  {formatArchiveTimestamp(s.createdAt)}
                </span>
              </div>
              <p className="text-[10px] font-mono tracking-[0.24em] uppercase text-muted-foreground/45">
                {s.archetypeName}
              </p>
              <p className="text-[12px] text-foreground/45 italic font-light leading-snug pt-0.5">
                {s.observation}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default RecentSessionsModule;
